<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-toolbar>
          <v-btn icon @click="$router.back()">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title> Rediger bruger </v-toolbar-title>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card :loading="loading">
          <v-toolbar flat>
            <v-toolbar-title>
              Generelt
            </v-toolbar-title>

            <v-spacer />

            <v-btn :loading="saving" @click="updateUser">
              {{ $t('common.save') }}
              <v-icon right>mdi-content-save-outline</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-form v-if="user">
              <v-row>
                <v-col cols="6" md="4">
                  <v-text-field
                    v-model="user.firstName"
                    label="Fornavn"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="4">
                  <v-text-field
                    v-model="user.lastName"
                    label="Efternavn"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="4">
                  <v-text-field
                    v-model="user.title"
                    label="Titel"
                    outlined
                  />
                </v-col>
                <v-col cols="6" md="4">
                  <v-text-field
                    v-model="user.email"
                    label="E-mail"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="4">
                  <v-autocomplete
                    v-model="user.primaryCustomerLocationId"
                    :items="locations"
                    :loading="!locations.length"
                    label="Primær lokation"
                    hint="OBS! Lige nu sat til lokation på anden kunde"
                    :persistent-hint="!locations.map(x => x.id).includes(user.primaryCustomerLocationId)"
                    item-text="title"
                    item-value="id"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            Roller
            <v-spacer></v-spacer>
            <div class="d-flex" style="gap: 1rem;">
              <v-btn small @click="showAllRoles = !showAllRoles">
                {{ showAllRoles ? 'Vis kun aktive' : 'Vis alle' }}
              </v-btn>
              <v-btn small @click="onAddRoleDialogOpen">
                Tildel rolle
                <v-icon right>mdi-plus</v-icon>
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text>
            <template v-for="roleCategory in user.roleCategories">
              <td
                v-for="(role, rIndex) in roleCategory.roles"
                :key="role.id"
                :style="rIndex==0 ? 'border-left: 2px solid black;' : null "
              >
                <PersonRoleEdit
                  :id="role.membership ? role.membership.id : null"
                  :person-id="user.id"
                  :role-id="role.id"
                  @saved="search"
                />
              </td>
            </template>

            <v-data-table
              :headers="roleHeaders"
              :items="roles.filter(x => this.showAllRoles || (!this.showAllRoles && x.isValid))"
            >
              <template #[`item.createdOn`]="{ value }">
                {{ value | formatDate }}
              </template>
              <template #[`item.validFrom`]="{ value }">
                {{ value | formatDate }}
              </template>
              <template #[`item.validTo`]="{ value }">
                {{ value | formatDate }}
              </template>
              <template #[`item.actions`]="{ item, index }">
                <v-btn
                  icon
                  :loading="removingRoleIndex === index"
                  @click="removeRole(item.id, index)"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            Lokationer
            <v-spacer></v-spacer>
            <div class="d-flex" style="gap: 1rem">
              <v-btn small @click="showAllLocations = !showAllLocations">
                {{ showAllLocations ? 'Vis kun aktive' : 'Vis alle' }}
              </v-btn>
              <v-btn
                small
                :disabled="!locations.some(x => x.isValid)"
                @click="onDeleteAllPersonLocation"
              >
                Slet alle
                <v-icon right>mdi-delete-sweep-outline</v-icon>
              </v-btn>
              <AddPersonLocationDialog :person-locations="personLocations" @success="getPersonLocations" />
            </div>
          </v-card-title>
          <v-card-subtitle>
            OBS! Hvis der tilføjes lokationer vil brugeren <strong>kun</strong> have adgang til de valgte lokationer. Hvis listen er tom vil brugeren have adgang til <strong>alle</strong> lokationer.
          </v-card-subtitle>

          <v-data-table
            :headers="locationHeaders"
            :items="personLocations.filter(x => this.showAllLocations || (!this.showAllLocations && x.isValid))"
            sort-by="customerLocationName"
            no-data-text="Brugeren har adgang til alle lokationer"
          >
            <template #[`item.validFrom`]="{ value }">
              {{ value | formatDateTime }}
            </template>
            <template #[`item.validTo`]="{ value }">
              {{ value | formatDateTime }}
            </template>
            <template #[`item.actions`]="{ item }">
              <div class="d-flex justify-end" style="gap: .5rem">                
                <v-btn v-if="item.isValid" icon color="error" @click="onDeletePersonLocation(item.id)">
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="addRoleDialog"
      max-width="800"
    >
      <v-card :loading="loadingRoles">
        <v-card-title> Tilføj rolle </v-card-title>
        <v-card-subtitle>
          Her kan du give brugeren en ny rolle.
        </v-card-subtitle>
        <v-card-text>
          <v-simple-table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>Kategori</th>
                <th>Ansvar</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(role, index) in assignableRoles" :key="role.id">
                <td>
                  {{ role.title }}
                </td>
                <td>
                  {{ role.category.title }}
                </td>
                <td>
                  <v-chip
                    v-if="!role.responsibilities"
                    label
                    small
                  >
                    Ingen ansvar
                  </v-chip>
                  <template v-else>
                    <v-chip
                      v-for="(responsibility, index) in role.responsibilities"
                      :key="index"
                      label
                      small
                    >
                      {{ responsibility }}
                    </v-chip>
                  </template>
                </td>
                <td align="right">
                  <v-btn
                    v-if="!roles.map(x => x.id).includes(role.id)"
                    :loading="assigningRoleIndex === index"
                    :disabled="assigningRoleIndex !== null && assigningRoleIndex !== index"
                    @click="assignRole(index)"
                  >
                    Tilføj
                  </v-btn>
                  <v-btn
                    v-else
                    :loading="assigningRoleIndex === index"
                    :disabled="assigningRoleIndex !== null && assigningRoleIndex !== index"
                    @click="removeRole(index)"
                  >
                    Fjern
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import AddPersonLocationDialog from '@/components/AddPersonLocationDialog.vue';
import TelInputComponent from '@/components/TelInputComponent.vue';

export default {
  name: 'UserEdit',
  components: { AddPersonLocationDialog, TelInputComponent },
  data() {
    return {
      user: null,
      roles: [],
      locations: [],
      personLocations: [],
      loading: true,
      saving: false,
      loadingRoles: false,
      addRoleDialog: false,
      assigningRoleIndex: null,
      removingRoleIndex: null,
      assignableRoles: [],
      showAllLocations: false,
      showAllRoles: false
    }
  },
  computed: {
    roleHeaders() {
      const result = [
        { text: 'Navn', value: 'title' },
        { text: 'Oprettet', value: 'createdOn' },
        { text: 'Gyldig fra', value: 'validFrom' },
        { text: 'Gyldig til', value: 'validTo' },
        { text: '', value: 'actions' }
      ]

      if (this.$store.getters.userHasMultipleCustomers) {
        result.splice(1, 0, { text: 'Kunde', value: 'customerTitle' })
      }

      return result
    },
    responsibilityHeaders() {
      const result = [
        { text: 'Navn', value: 'responsibilityTitle' },
        { text: 'Oprettet', value: 'createdOn' },
        { text: '', value: 'actions', align: 'end' },
      ]

      if (this.$store.getters.userHasMultipleCustomers) {
        result.splice(1, 0, { text: 'Kunde', value: 'customerTitle' })
      }

      return result
    },
    locationHeaders () {
      return [
        { text: 'Lokation', value: 'customerLocationName' },
        { text: 'Type', value: 'customerLocationType' },
        { text: 'Gyldig fra', value: 'validFrom' },
        { text: 'Gyldig til', value: 'validTo' },
        { text: '', value: 'actions', align: 'end' }
      ]
    }
  },
  methods: {
    getUser: async function() {
      const userId = this.$route.params.id;

      try {
        const { data } = await this.$api.get('users/' + userId)

        this.user = data
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false
      }
    },
    getLocations: async function() {
      try {
        const { data } = await this.$api.get('locations', {
          params: {
            customerId: this.$store.state.primaryCustomerId
          }
        });

        this.locations = data;
      } catch (error) {
        console.error(error);
      }
    },
    getPersonLocations: async function() {
      const userId = this.$route.params.id;

      try {
        const { data } = await this.$api.get('person-locations', {
          params: {
            userId
          }
        });

        this.personLocations = data;
      } catch (error) {
        console.error(error);
      }
    },
    getRoles: async function() {
      const userId = this.$route.params.id;

      try {
        const { data } = await this.$api.get('person-customer-roles', {
          params: {
            userId
          }
        });

        this.roles = data;
      } catch (error) {
        console.error(error);
      }
    },
    onAddRoleDialogOpen: async function() {
      this.addRoleDialog = true

      try {
        this.loadingRoles = true
        const { data } = await this.$api.get(`organizations/assignable-roles/${this.$route.params.id}`)

        this.assignableRoles = data
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loadingRoles = false
      }
    },
    updateUser: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch('users/' + this.$route.params.id, this.user);

        if (!data.success) {
          console.error(data.errors);
          throw 'Error updating user'
        }

        this.$notifier.showSuccess({ message: 'Brugeren blev gemt' });
        this.getUser();
      } catch (error) {
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    assignRole: async function(index) {
      this.assigningRoleIndex = index;

      try {
        const role = this.assignableRoles[index];

        if (!role) throw new Error("Could not find role at index " + index);

        const model = {
          personId: this.user.id,
          customerRoleId: role.id
        };

        const { data } = await this.$api.post('person-customer-roles', model);

        if (!data.success) {
          console.error(data.errors)
          throw 'Error assigning role'
        }

        this.$notifier.showSuccess({ message: 'Rollen blev tildelt' });
        this.getRoles();
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.assigningRoleIndex = null;
      }
    },
    removeRole: async function(id, index) {
      this.removingRoleIndex = index

      try {
        const { data } = await this.$api.delete('person-customer-roles/' + id);

        if (!data.success) {
          console.error('Errors:', data.errors);
          throw 'Error deleting role';
        }

        await this.getRoles();

        this.$notifier.showSuccess({ message: 'Rollen blev fjernet' })
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.removingRoleIndex = null;
      }
    },
    onDeletePersonLocation: async function(personLocationId) {
      if (confirm('Er du sikker på du vil slette?')) {
        try {
          const model = {
            validTo: new Date().toISOString()
          }
          const { data } = await this.$api.patch('person-locations/' + personLocationId, model)

          if (!data.success || data.errors.length) throw data.message

          this.$notifier.showSuccess({ message: 'Lokationen blev slette fra brugeren' });
          this.getPersonLocations();
        } catch (error) {
          console.error(error)
          this.$notifier.showError({ message: 'Kunne ikke slette' })
        }
      }
    },
    onDeleteAllPersonLocation: async function() {
      if (confirm('Er du sikker? Brugeren vil efter dette have adgang til alle lokationer.')) {
        const active = this.user.locations.filter(x => x.isValid)

        try {
          for (const item of active) {
            const model = {
              validTo: new Date().toISOString()
            }
  
            const { data } = await this.$api.patch('person-locations/' + item.id, model)
  
            if (!data.success || data.errors.length) throw data.message
  
            this.$notifier.showSuccess({ message: 'Lokationen blev slette fra brugeren' });
          }
        } catch (error) {
          console.error(error)
          this.$notifier.showError({ message: 'Kunne ikke slette' })
        } finally {
          this.getUser();
        }
      }
    }
  },
  mounted: function() {
    this.getUser();
    this.getRoles();
    this.getPersonLocations();
    this.getLocations();
  }
}
</script>